import { lazy, Suspense, useEffect } from "react";
import { Brand } from "./components/Brand";
import { useAuthStore } from "./stores/authStore";
import { useUiStore } from "./stores/uiStore";
import { useOrgStore } from "./stores/orgStore";
import { hydrateFlagDismissals } from "./lib/flagDismissals";
import { useSoilUnits } from "./stores/soilUnitsStore";
import { useTerrainPref } from "./stores/terrainPrefStore";
import { useEnhancedVisuals } from "./stores/enhancedVisualsStore";
import { useUiMode } from "./stores/uiModeStore";
import { api } from "./api";
import { useExtractantFactorsQuery } from "./hooks/useExtractantFactors";
import { useMe } from "./hooks/queries";
import { useJobTracker } from "./hooks/useJobTracker";
import { useMergeWatcher } from "./hooks/useMergeWatcher";
import { FieldsTable } from "./components/FieldsTable";
import { Login } from "./features/sign-in/Login";
import { GlobalDropZone } from "./features/global-drop/GlobalDropZone";
import { JdPullModal } from "./features/jd-pull/JdPullModal";
import { NewFieldJdPrompt } from "./features/new-field-jd/NewFieldJdPrompt";
import { NoOrgGate } from "./features/org/NoOrgGate";
import { ProfileMenu } from "./components/ProfileMenu";
import { ReachabilityNote } from "./features/reachability/ReachabilityNote";
import { WizardHost } from "./features/wizard/WizardHost";
import {
  FieldToolsFilter,
  FieldToolsMenu,
} from "./features/field-tools/FieldTools";
import { SamplingConfirmDialog } from "./features/sampling-confirm/SamplingConfirmDialog";
import { WorkOrdersPage } from "./features/work-orders/WorkOrdersPage";
import { BulkDumpPanel } from "./components/BulkDumpPanel";
import { ErrorsMenu } from "./features/errors/ErrorsMenu";
import { SystemDashboard } from "./features/system-dashboard/SystemDashboard";
import { BoundaryOverlapConfirm } from "./features/boundary-overlap/BoundaryOverlapConfirm";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Modal } from "./ui/Modal";

// The map engine (~800KB) is a SEPARATE lazy chunk — the table-only initial load
// never ships it (Front-End.md: map is an optional, lazy window).
const MapWindow = lazy(() => import("./components/MapWindow"));
const DrawFieldModal = lazy(
  () => import("./features/draw-field/DrawFieldModal"),
);

export function App() {
  const session = useAuthStore((s) => s.session);
  const status = useAuthStore((s) => s.status);
  const mapOpen = useUiStore((s) => s.mapOpen);
  const selectedFieldId = useUiStore((s) => s.selectedFieldId);
  const closeMap = useUiStore((s) => s.closeMap);
  const newFieldModal = useUiStore((s) => s.newFieldModal);
  const closeNewField = useUiStore((s) => s.closeNewField);
  const workOrdersOpen = useUiStore((s) => s.workOrdersOpen);
  const closeWorkOrders = useUiStore((s) => s.closeWorkOrders);
  const bulkDump = useUiStore((s) => s.bulkDump);
  const errorsMenuOpen = useUiStore((s) => s.errorsMenu);
  const closeErrorsMenu = useUiStore((s) => s.closeErrorsMenu);
  const systemDashboardOpen = useUiStore((s) => s.systemDashboard);
  const closeSystemDashboard = useUiStore((s) => s.closeSystemDashboard);
  const closeBulkDump = useUiStore((s) => s.closeBulkDump);
  const orgs = useOrgStore((s) => s.orgs);
  const orgHydrated = useOrgStore((s) => s.hydrated);
  // the header toolbar acts on a fields list, which only exists once an org is resolved
  const toolsReady = orgHydrated && orgs.length > 0;
  const orgError = useOrgStore((s) => s.error);
  // These hooks sit above the session return, so they run on the loading
  // screen and on Login. Authenticated reads wait until a session exists.
  const signedIn = session != null;
  useJobTracker(signedIn);
  useMergeWatcher(signedIn);

  // Reset ONLY when genuinely logged out (status 'idle'), NOT during the initial 'booting' pass
  // while bootstrap restores the session on reload — resetting there wiped the persisted active org
  // before hydrate could keep it.
  const userId = session?.user_id;
  useEffect(() => {
    if (userId) void useOrgStore.getState().hydrate();
    else if (status === "idle") useOrgStore.getState().reset();
  }, [userId, status]);

  // Action-flag dismissals are PER JOB (field x flag x year), not per user — so the server copy is the
  // shared truth and every client must READ it, not just write it. Hydrate on login and on every org
  // switch (dismissals belong to that org's fields); server wins over the local cache. Without this the
  // feature is localStorage-only in practice: a teammate's dismissal never arrives and clearing storage
  // resurrects flags the server still has dismissed.
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  useEffect(() => {
    if (userId && activeOrgId) void hydrateFlagDismissals();
  }, [userId, activeOrgId]);

  // The soil-units lens is per-user state and the server is its record. The extractant factors
  // are served data: the query below owns them, and a cached set keeps the lens working until it lands.
  useExtractantFactorsQuery(signedIn);

  const me = useMe(signedIn);
  // `preferences` is the only shape now — BE-Light dropped the `soil_units` alias on (verified
  // absent from /users/me), so the fallback that carried the rename is gone with it.
  const prefsFromServer = me.data?.preferences;
  useEffect(() => {
    if (prefsFromServer !== undefined) {
      useSoilUnits.getState().hydrateFromServer(prefsFromServer);
      useTerrainPref
        .getState()
        .hydrateFromServer(prefsFromServer as Record<string, unknown>);
      useEnhancedVisuals
        .getState()
        .hydrateFromServer(prefsFromServer as Record<string, unknown>);
      useUiMode
        .getState()
        .hydrateFromServer(prefsFromServer as Record<string, unknown>);
    }
  }, [prefsFromServer]);

  // ORG DEFAULT SOIL UNITS: fetched per active org; the store applies it only to a user with no
  // lens of their own (untouched + no server lens + no local record) — precedence user's own > org
  // default > system.
  const activeOrgIdForUnits = useOrgStore((s) => s.activeOrgId);
  useEffect(() => {
    if (!session || !activeOrgIdForUnits) return;
    let live = true;
    console.debug("[org-units] fetching org default for", activeOrgIdForUnits);
    api
      .fetchOrgSoilUnits(activeOrgIdForUnits)
      .then((r) => {
        if (live) useSoilUnits.getState().hydrateOrgDefault(r);
      })
      .catch((e) => {
        console.debug("[org-units] org default fetch FAILED", e);
        if (live) useSoilUnits.getState().hydrateOrgDefault(null);
      });
    return () => {
      live = false;
    };
  }, [session, activeOrgIdForUnits]);

  if (!session) {
    if (status === "booting")
      return (
        <div className="flex h-full items-center justify-center text-sm text-faint">
          Loading…
        </div>
      );
    return <Login />;
  }

  return (
    <div className="flex h-full flex-col bg-canvas text-fg">
      <WizardHost />
      {/* Header: the flat menu beside the logo, the filter box beside the profile menu and nothing in between — Map Soil / Build Rx / the count moved back down into the page, where a raised bar can actually highlight them. */}
      <header className="flex items-center gap-3 border-b border-line bg-surface px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <Brand />
          {/*
 The toolbar renders only with an org in hand: its buttons act on a fields list that doesn't
              exist before then.
*/}
          {toolsReady && (
            <ErrorBoundary label="field menu">
              <FieldToolsMenu />
            </ErrorBoundary>
          )}
        </div>

        <div className="ml-auto flex min-w-0 items-center justify-end gap-3 text-xs text-muted">
          {toolsReady && (
            <ErrorBoundary label="field filter">
              <FieldToolsFilter />
            </ErrorBoundary>
          )}
          <ProfileMenu />
        </div>
      </header>
      <ReachabilityNote />

      <main className="min-h-0 flex-1 overflow-auto p-3 sm:p-4">
        {!orgHydrated ? (
          <div className="flex h-full items-center justify-center text-sm text-faint">
            Loading your organizations…
          </div>
        ) : orgError && orgs.length === 0 ? (
          // a transient load failure is NOT the same as genuinely having no orgs — offer a retry
          // instead of wrongly telling the user to link/create one.
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-sm">
            <p className="text-muted">
              Couldn't load your organizations.
              <br />
              <span className="text-[12px] text-faint">{orgError}</span>
            </p>
            <button
              className="rounded bg-accent px-3 py-1.5 text-xs font-medium text-canvas hover:bg-accent-hover"
              onClick={() => void useOrgStore.getState().hydrate()}
            >
              Retry
            </button>
          </div>
        ) : orgs.length === 0 ? (
          <NoOrgGate />
        ) : (
          <FieldsTable />
        )}
      </main>

      {mapOpen && (
        <ErrorBoundary
          label="map"
          resetKey={selectedFieldId}
          onReset={closeMap}
        >
          <Suspense
            fallback={
              <Modal>
                <div className="fixed inset-0 z-panel flex items-center justify-center bg-black/40 text-white">
                  Loading map…
                </div>
              </Modal>
            }
          >
            <MapWindow />
          </Suspense>
        </ErrorBoundary>
      )}

      {workOrdersOpen && (
        <ErrorBoundary
          label="work orders"
          resetKey="work-orders"
          onReset={closeWorkOrders}
        >
          <WorkOrdersPage />
        </ErrorBoundary>
      )}

      {bulkDump && (
        <ErrorBoundary
          label="bulk data dump"
          resetKey={bulkDump}
          onReset={closeBulkDump}
        >
          <BulkDumpPanel />
        </ErrorBoundary>
      )}

      {/*
 Errors menu + System Dashboard own their OWN open state (uiStore) and must mount unconditionally.
          They were briefly nested inside the bulk-dump conditional above, which meant neither could ever
          open unless a bulk drop happened to be on screen — each has its own ErrorBoundary so one failing
          can't take the other (or the table) down.
*/}
      {errorsMenuOpen && (
        <ErrorBoundary
          label="errors menu"
          resetKey="errors-menu"
          onReset={closeErrorsMenu}
        >
          <ErrorsMenu />
        </ErrorBoundary>
      )}
      {systemDashboardOpen && (
        <ErrorBoundary
          label="system dashboard"
          resetKey="system-dashboard"
          onReset={closeSystemDashboard}
        >
          <SystemDashboard />
        </ErrorBoundary>
      )}

      {newFieldModal === "draw" && (
        <ErrorBoundary
          label="draw tool"
          resetKey={newFieldModal}
          onReset={closeNewField}
        >
          <Suspense
            fallback={
              <Modal>
                <div className="fixed inset-0 z-dialog flex items-center justify-center bg-black/40 text-white">
                  Loading…
                </div>
              </Modal>
            }
          >
            <DrawFieldModal />
          </Suspense>
        </ErrorBoundary>
      )}
      {newFieldModal === "jd" && <JdPullModal />}

      <GlobalDropZone />
      <NewFieldJdPrompt />
      {/* Sampling-order confirmation: shown before any sampling is ordered, from any flow. */}
      <SamplingConfirmDialog />
      {/* BoundaryOverlapConfirm uses z-confirm so it stays above the other dialogs. Portals append in mount order, so source order alone no longer decides which z-dialog paints on top. */}
      <BoundaryOverlapConfirm />
    </div>
  );
}
